import { useState, type InputHTMLAttributes, type KeyboardEvent } from "react";

interface Props {
  value: string;
  /** A name for screen readers when the field has no label around it. */
  label?: string;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  inputMode?: InputHTMLAttributes<HTMLInputElement>["inputMode"];
  maxLength?: number;
  autoComplete?: string;
  /**
   * Saves the typed text. Returning false keeps the text in the field to be corrected (a refused
   * value, with the reason shown next to it); otherwise the field shows the saved value again.
   */
  onSave: (text: string) => unknown;
  /** The text was edited back to the saved value (e.g. a refused value corrected): nothing to save. */
  onSame?: () => void;
}

/** A field that saves when it is left or with Enter (the prototype's onchange). */
export function SaveInput({
  value,
  label,
  placeholder,
  className,
  disabled,
  inputMode,
  maxLength,
  autoComplete,
  onSave,
  onSame,
}: Props) {
  const [text, setText] = useState<string | null>(null);
  const done = () => {
    if (text === null || text === value) {
      if (text !== null) onSame?.();
      setText(null);
      return;
    }
    if (onSave(text) !== false) setText(null);
  };
  return (
    <input
      value={text ?? value}
      aria-label={label}
      placeholder={placeholder}
      className={className}
      disabled={disabled}
      inputMode={inputMode}
      maxLength={maxLength}
      autoComplete={autoComplete}
      onChange={(event) => {
        setText(event.target.value);
      }}
      onBlur={done}
      onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") event.currentTarget.blur();
      }}
    />
  );
}
